import { storefrontAccessToken, storefrontAccessTokenDev } from '../config';

const prefixGraphQLVariant = 'gid://shopify/ProductVariant/';

const buildGraphQLVariantId = (variantId) => {
  return `${prefixGraphQLVariant}${variantId}`;
};

const QUERY_PRODUCTS_BY_NODE = `
  query ProductsByNode($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on ProductVariant {
        id
        barcode
        compareAtPrice {
          amount
        }
        price {
          amount
        }
        product {
          productType
        }
      }
    }
  }
`;

/**
 * Bring and parse cart line items data
 * @param {string} graphQLCartId - GraphQL Cart id
 * @param {boolean} isInternational - is it international
 * @returns {Array<Object>} - set of value per line item in cart lines
 */
const getCart = () => {
  const shopScopedFinalsale = JSON.parse(document.getElementById('shop-scoped-finalsale-metafields')?.innerHTML);
  const cart = window.cart;
  const cartLineItems = cart.items.map((item) => ({
    cost: {
      subtotalAmount: {
        amount: (item.original_price / 100).toFixed(2),
      },
      totalAmount: {
        amount: (item.final_price / 100).toFixed(2),
      }
    },
    discount: item.discounts.map((discount) => discount.title),
    variantId: item.variant_id.toString(),
  }));

  return {
    order: {
      cost: {
        subtotalAmount: {
          amount: (cart.original_total_price / 100).toFixed(2),
        },
        totalAmount: {
          amount: (cart.total_price / 100).toFixed(2),
        }
      },
      discountCodes: cart.cart_level_discount_applications.map((code) => code.title),
      excludedDiscountCodes: shopScopedFinalsale.discountExcludeFinalSale,
      includedDiscountCodes: shopScopedFinalsale.discountIncludeFinalSale,
    },
    lineItems: cartLineItems,
  };
};

/**
 * Build product nodes from variants and its corresponding final sale metafield array
 * @param {Array<Object>} variants - array of variant objects
 * @param {Object} product - product object data
 * @param {Array<Object>} variantFinalSaleMetafieldArray - array of variant final sale metafields
 * @returns {Array<Object>} - array of product nodes
 */
export const buildProductNodes = (variants, product, variantFinalSaleMetafieldArray) => {
  return variants.map((variant) => ({
    dataset: {
      price: variant.price,
      barcode: variant.barcode,
      id: variant.id,
      compareAtPrice: variant.compare_at_price,
      productType: product.v || product.type || variantFinalSaleMetafieldArray[0]?.productType || '',
      variantScopedFinalSaleMetafield:
        variantFinalSaleMetafieldArray.find(
          (item) =>
            Number(item.id) === variant.id ||
            (typeof variant.id === 'string' &&
              variant.id.split('-').slice(2).join('-') === item.options.join('-').toLowerCase()),
        )?.variantFinalSaleMetafield || '',
    },
  }));
};

/**
 * Build and parse a consistent array of variant object with minimum information
 * @param {Array<Object>} productNodes - array of objects with variant data
 * @returns {Array<Object>} - array of variant objects with minimum data
 */
const getPrices = (productNodes) => {
  return Array.from(productNodes).map((node) => ({
    id: node.dataset.id?.toString() || node.dataset.variantId?.toString(), // PDP Redesign brings dataset.variantId instead of dataset.id
    barcode: node.dataset.barcode,
    compareAtPrice: (Number(node.dataset.compareAtPrice) / 100).toFixed(2),
    price: (Number(node.dataset.price) / 100).toFixed(2),
    productType: node.dataset.productType,
    finalSale: node.dataset.variantScopedFinalSaleMetafield,
  }));
};

/**
 * Bring and parse variants data in US currency
 * @param {Array<string>} graphQLVariantIds - array of GraphQL variant ids
 * @param {Array<Object>} productNodes - array of objects with variant data
 * @returns {Array<Object>} - set of values per variant
 */
const getUSDPrices = async (graphQLVariantIds, productNodes) => {
  const body = JSON.stringify({ query: QUERY_PRODUCTS_BY_NODE, variables: { ids: graphQLVariantIds } });

  const response = await commonFetch(body);

  return response.data.nodes.map((node) => ({
    id: node.id.replace(prefixGraphQLVariant, ''),
    barcode: node.barcode,
    compareAtPrice: node.compareAtPrice.amount,
    price: node.price.amount,
    productType: node.product.productType,
    finalSale: productNodes.find(item => ((typeof item.id === 'string' && item.id === node.id.replace(prefixGraphQLVariant, '')) || item.id === Number(node.id.replace(prefixGraphQLVariant, ''))))?.variantScopedFinalSaleMetafield || '',
  }));
};

/**
 * Get data from Shopify Market
 * @param {string} body - establish GraphQL query and GraphQL variables
 * @returns {Object} - response data
 */
const commonFetch = async (body) => {
  let storefront_access_token = storefrontAccessTokenDev;
  if (window.Shopify.shop === 'windsor-us.myshopify.com') {
    storefront_access_token = storefrontAccessToken;
  }

  const response = await window.fetch(`https://${window.Shopify.shop}/api/2023-07/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': storefront_access_token,
    },
    body,
  });

  const object = await response.json();

  if (!object || !object.data) {
    throw new Error(`No data found in Shopify for theses ids: ${body.variables.ids}`);
  }

  return object;
};

/**
 * @description Function to get the float value with upto two decimal points.
 * @param {number} value - The value to be converted.
 * @returns {number} The float value with upto two decimal points.
 */
const getTwoDecimalFloatValue = (value) => parseFloat(value.toFixed(2));

const finalSaleCalculation = ({ variant, cartFlag, cart, hasGivexDigital }) => {
  let isFinalSale = false;
  let finalSaleCode = '';

  // Line items's sale price before any discount applied in shop currency ($)
  const price = Number(variant.price);
  const compareAtPrice = Number(variant.compareAtPrice);
  const barcode = variant.barcode;
  const isGivexDigital = variant && variant.productType.includes('GIVEX');
  // Variable for variant-scoped final sale value
  const finalSaleSetting = variant?.finalSale;

  let productDiscountPercentage = 0;
  let employeeDiscountPercentage = 0;

  let appliedDiscountCode = 0;
  let orderDiscountPercentage = 0;

  // Variables to set shop-scoped metafield values
  let excludedDiscountCodes;
  let includedDiscountCodes;

  // Variable for Condition 3: Discount percentage for compare at price vs sale price
  const markdownDiscountPercentage = getTwoDecimalFloatValue(((compareAtPrice - price) / compareAtPrice) * 100);

  if (cartFlag) {
    // Calculates variable for Condition 2(a): Amount of PRODUCT discount percentage (Product discount coupon code), if any applied on the line item
    const cartItem = cart.lineItems.find((lineItem) => lineItem.variantId === variant.id);
    const cartItemBeforeDiscount = Number(cartItem.cost.subtotalAmount.amount);
    const originalPrice = compareAtPrice ? compareAtPrice : price;
    const cartItemAfterDiscount = Number(cartItem.cost.totalAmount.amount);
    productDiscountPercentage = getTwoDecimalFloatValue(((originalPrice - cartItemAfterDiscount) / originalPrice) * 100);

    // Calculates variables for Condition 2(b): Amount of ORDER discount percentage (Order discount coupon code), if any applied on the cart
    appliedDiscountCode = cart.order.discountCodes.at(0) || cart.lineItems.map((item) => item.discount).flat().at(0); // get first discount applied at line item level because it is the same on the rest of line items
    const cartAmountBeforeDiscount = Number(cart.order.cost.subtotalAmount.amount);
    const cartAmountAfterDiscount = Number(cart.order.cost.totalAmount.amount);
    // 'amount off order' discount <= 30% AND markdown discount <= 30% BUT total discount > 30%, it should qualify for final sale
    orderDiscountPercentage = getTwoDecimalFloatValue((((cartAmountBeforeDiscount - cartAmountAfterDiscount) / cartAmountBeforeDiscount) * 100) + markdownDiscountPercentage);

    // Store excluded and included discount codes
    excludedDiscountCodes = cart.order.excludedDiscountCodes;
    includedDiscountCodes = cart.order.includedDiscountCodes;

    // Calculates variable for employee discount condition
    employeeDiscountPercentage = getTwoDecimalFloatValue(((cartItemBeforeDiscount - cartItemAfterDiscount) / cartItemBeforeDiscount) * 100);
  }

  // Track hard final-sale reasons we don't want to override (markdown/price endings, barcode, intrinsic compare-at vs sale)
  const isPriceEndingFinalSale = price.toFixed(2).endsWith('.45') ||
    price.toFixed(2).endsWith('.96') ||
    price.toFixed(2).endsWith('.97');
  // Goes over if style-number starts with some of these prefixes.
  const isBarcodeFinalSale = !!barcode && (
    barcode.startsWith('07001') ||
    barcode.startsWith('07101') ||
    barcode.startsWith('07102') ||
    barcode.startsWith('07108') ||
    barcode.startsWith('07307')
  );

  if (isPriceEndingFinalSale) {
    // Condition 1: itemSalePriceInTwoDecimals (in USD, before discounts, regardless of cart qty) ends in: .45, .96, .97
    isFinalSale = true;
    finalSaleCode = 'BY_PRICE_ENDS';
  } else if (productDiscountPercentage > 30) {
    // Condition 2 (a): Checking for amount off product discount percentage > 30%
    isFinalSale = true;
    finalSaleCode = 'BY_PRODUCT_DISCOUNT';
  } else if (orderDiscountPercentage > 30) {
      // Condition 2 (b): Checking for amount off order discount percentage > 30%
    isFinalSale = true;
    finalSaleCode = 'BY_ORDER_DISCOUNT';
  } else if (markdownDiscountPercentage > 30) {
    // Condition 3: Markdown percentage > 30% for item's compareAtPrice vs sellPrice
    isFinalSale = true;
    finalSaleCode = 'BY_MARKDOWN_DISCOUNT';
  } else if (isBarcodeFinalSale){
    // Condition 4: Barcode starts with 07001, 07101, 07102, 07108, or 07307
    isFinalSale = true;
    finalSaleCode = 'BY_STYLE_NUMBER';
  }

  if (
    cartFlag &&
    appliedDiscountCode &&
    includedDiscountCodes.length &&
    includedDiscountCodes.includes(appliedDiscountCode)
  ) {
    // Condition shop-scoped metafield 'fso.discount_include_finalsale': set 'Final Sale' if there was a coupon code that matched in this list
    isFinalSale = true;
    finalSaleCode = 'BY_INCLUDED_DISCOUNT_CODE';
  }

  if (
    cartFlag &&
    appliedDiscountCode &&
    excludedDiscountCodes.length &&
    excludedDiscountCodes.includes(appliedDiscountCode)
  ) {
    // Condition shop-scoped metafield 'fso.discount_exclude_finalsale': remove 'Final Sale' if there was a coupon code that matched in this list
    isFinalSale = false;
    finalSaleCode = 'BY_EXCLUDED_DISCOUNT_CODE';
  }

  if (finalSaleSetting) {
    // Condition variant-scoped metafield 'fso.finalsale': force to set 'Final Sale' either 'true' or 'false' value regardless previous conditions
    isFinalSale = finalSaleSetting === 'true' || false;
    finalSaleCode = 'BY_VARIANT_FINAL_SALE_SETTING';
  }

  // If any employee discount code is applied where Code Length is 4 characters, and discount percentage is 40%, then isFinalSale is false
  if (
    cartFlag &&
    appliedDiscountCode &&
    appliedDiscountCode.length === 4 &&
    ((employeeDiscountPercentage >= 39.9 && employeeDiscountPercentage <= 40.1) || (orderDiscountPercentage >= 39.9 && orderDiscountPercentage <= 40.1))
  ) {
    isFinalSale = false;
  }

  // If any GIVEX - DIGITAL item is in the cart, only suppress coupon-driven final sale for non-GIVEX items.
  // Preserve hard final sale from markdown/compare-at or barcode.
  const hasHardFinalSaleReason = isPriceEndingFinalSale || markdownDiscountPercentage || isBarcodeFinalSale;
  if (cartFlag && hasGivexDigital && !isGivexDigital && !hasHardFinalSaleReason) {
    isFinalSale = false;
  }

  return { value: isFinalSale, code: finalSaleCode };
};

/**
 * Determine and set which variants qualify for final sale
 * @param {Object} param0 - list of variant ids, cart flag, is it international? And set of product data per nodes
 * @returns {Array<Object>} - list of variants with its corresponding qualification
 */
export const finalSaleCalculate = async ({ cartFlag, isInternational, productNodes }) => {
  return new Promise(async (resolve, reject) => {
    try {
      // Get variants prices in current currency
      const variantsPrices = await getPrices(productNodes);
      // Check if any cart item is a GIVEX - DIGITAL product
      const hasGivexDigital = Array.isArray(variantsPrices) && variantsPrices.some(v => v?.productType.includes('GIVEX'));
      // Get cart data
      const cart = cartFlag ? getCart() : undefined;

      // Iterating over each cart line item to check for final sale message conditions
      let variantsValidated = variantsPrices.map((variant) => {
        const finalSaleResult = finalSaleCalculation({ variant, cartFlag, cart, hasGivexDigital });

        return {
          ...variant,
          finalSale: finalSaleResult.value,
          finalSaleCode: finalSaleResult.code,
        };
        });

      // Stop going over final sale calculation here if it wasn't an international user
      if (!isInternational) return resolve(variantsValidated);

      // Go over if all of them in array qualified for final sale. Otherwise, we will proceed to make a network request to get USD prices
      if (variantsValidated.every(i => i?.finalSale)) {
        return resolve(variantsValidated);
      }

      // Get only variant ids whose final sale properties were false
      const variantsWithoutFinalSale = variantsValidated.filter((variant) => !variant?.finalSale);
      const graphQLVariantIds = variantsWithoutFinalSale.map((variant) => buildGraphQLVariantId(variant.id));
      // Filtered variants whose final sale properties were true
      variantsValidated = variantsValidated.filter((variant) => variant?.finalSale);
      // Filtered variants scoped by metafield whose final sale properties were false
      const filteredVariantsScopedMetafield = Array.from(productNodes).map(node => ({id: node.dataset.id?.toString() || node.dataset.variantId?.toString(), variantScopedFinalSaleMetafield: node.dataset.variantScopedFinalSaleMetafield }))

      const variantsUSDPrices = await getUSDPrices(
        graphQLVariantIds,
        filteredVariantsScopedMetafield,
      );
      // Concat validated variants that already had a true final sale with variantsUSDPrices and Iterating over each cart line item to check for final sale message conditions
      variantsValidated = variantsValidated.concat(variantsUSDPrices.map((variant) => {
        const foundVariant = variantsWithoutFinalSale.find((item) => variant.id === item.id);
        const finalSaleResult = finalSaleCalculation({ variant, cartFlag, cart, hasGivexDigital });

        return {
          ...variant,
          price: foundVariant?.price, // bring price in current currency
          compareAtPrice: foundVariant?.compareAtPrice, // bring compare at price in current currency
          finalSale: finalSaleResult.value,
          finalSaleCode: finalSaleResult.code,
        };
        }));
      return resolve(variantsValidated);
    } catch (error) {
      reject(error);
    }
  })
};